import {updateClientUser, updateEmailNewsletterSubscription, updateUser, updateUserPassword} from "../../../api/graphql/user";
import {getBankInfo, updateBankInfo} from "../../../api/graphql/bankInformation";
import {useRecoilState, useSetRecoilState} from "recoil";
import {globalNotificationState, globalUserDetailState} from "../../../_common/global_state";
import {selfBankInfoAccountState} from "../state";
import {useVerification} from "../../../_common/_authorize";
import {useTranslation} from "react-i18next";
import { createLogSession } from "../../../api/restful/logs/logs";

const useProfileFunction = () => {
    const {t} = useTranslation()
    const {baseErrorChecking} = useVerification()

    const [userDetails, setUserDetailState] = useRecoilState(globalUserDetailState);
    const setBankInfo = useSetRecoilState(selfBankInfoAccountState)
    const setNotificationMessage = useSetRecoilState(globalNotificationState);

    const getBankDetails = async () => {
        try {
            const bankDetail = await getBankInfo(userDetails?._id)
            setBankInfo(bankDetail.getBankInformation.data)
        } catch (e) {
            console.error("Error:", e)
            setNotificationMessage({
                status: "error",
                title: "",
                msg: "System Error",
                key: "systemNotification",
            });
        }

    }

    const updatePassword = async (id, value) => {
        const log = createLogSession("User", "Profile", "updatePassword")
        try {
            const response = await updateUserPassword(id, value)
            if (response?.updateUserPassword?.__typename === "BaseError") {
                log.create({ status: "error", description: `Password update failed: ${response?.updateUserPassword?.errKey}`, logData: response?.updateUserPassword })
                return await baseErrorChecking(response?.updateUserPassword?.errKey)

            } else {
                setUserDetailState(response?.updateUserPassword)
                setNotificationMessage({
                    status: "success",
                    title: "",
                    msg: "Updated Successfully",
                    key: "profileNotification",
                });
                log.create({ status: "info", description: "Password updated", logData: { ...value } })
            }
        } catch (e) {
            log.create({ status: "error", description: "Password update exception", logData: e })
            console.error("Error:", e)
            setNotificationMessage({
                status: "error",
                title: "",
                msg: "System Error",
                key: "systemNotification",
            });
        }

    }

    const updateProfile = async (id, value, isUpdatingLanguage = false) => {
        const log = createLogSession("User", "Profile", "updateProfile")
        try {
            const response = await updateClientUser(id, value)
            if (response?.updateClientUser?.__typename === "BaseError") {
                log.create({ status: "error", description: `Profile update failed: ${response?.updateClientUser?.errKey}`, logData: response?.updateClientUser })
                return await baseErrorChecking(response?.updateClientUser?.errKey)

            } else {
                setUserDetailState(response?.updateClientUser)
                log.create({ status: "info", description: "Profile updated", logData: { ...value } })
                if (isUpdatingLanguage) {
                    return response?.updateClientUser
                } else {
                    setNotificationMessage({
                        status: "success",
                        title: "",
                        msg: "Updated Successfully",
                        key: "profileNotification",
                    });
                }
            }
        } catch (e) {
            log.create({ status: "error", description: "Profile update exception", logData: e })
            console.error("Error:", e)
            setNotificationMessage({
                status: "error",
                title: "",
                msg: "System Error",
                key: "systemNotification",
            });
        }

    }

    const updateUserProfile = async (id, value) => {
        const log = createLogSession("User", "Profile", "updateUserProfile")
        try {
            const response = await updateUser(id, value)
            if (response?.updateUser?.__typename === "BaseError") {
                log.create({ status: "error", description: `User profile update failed: ${response?.updateUser?.errKey}`, logData: response?.updateUser })
                return await baseErrorChecking(response?.updateUser?.errKey)
            } else {
                setUserDetailState(response?.updateUser)
                setNotificationMessage({
                    status: "success",
                    title: "",
                    msg: "Updated Successfully",
                    key: "profileNotification",
                });
                log.create({ status: "info", description: "User profile updated", logData: { ...value } })
            }
        } catch (e) {
            log.create({ status: "error", description: "User profile update exception", logData: e })
            console.error("Error:", e)
            setNotificationMessage({
                status: "error",
                title: "",
                msg: "System Error",
                key: "systemNotification",
            });
        }

    }

    const updateUserNewsletterSettings = async (input) => {
        const log = createLogSession("User", "Profile", "updateUserNewsletterSettings")
        try {
            const {mode} = input
            const response = await updateEmailNewsletterSubscription(input)
            if (response?.updateEmailNewsletterSubscription?.__typename === "BaseError") {
                log.create({ status: "error", description: `Newsletter update failed: ${response?.updateEmailNewsletterSubscription?.errKey}`, logData: response?.updateEmailNewsletterSubscription })
                return await baseErrorChecking(response?.updateEmailNewsletterSubscription?.errKey)
            } else {
                setNotificationMessage({
                    status: "success",
                    title: "",
                    msg: t(`common.${mode}`) + " Successfully",
                    key: "profileNotification",
                });
                log.create({ status: "info", description: `Newsletter settings updated (${mode})`, logData: { ...input } })
            }
        } catch (e) {
            log.create({ status: "error", description: "Newsletter settings update exception", logData: e })
            console.error("Error:", e)
            setNotificationMessage({
                status: "error",
                title: "",
                msg: "System Error",
                key: "systemNotification",
            });
        }

    }

    function validateInput(input) {
        // Regular expression to allow only alphanumeric characters
        const regex = /^[a-zA-Z0-9 ]+$/;

        return regex.test(input);
    }

    const validateKeys = ["account_holder", "account_no"]

    const updateBankDetail = async (userid, modifiedObject, id) => {
        const log = createLogSession("User", "Profile", "updateBankDetail")
        try {
            const invalidKey = []
            Object.keys(modifiedObject)?.map(d => {
                if (validateKeys.includes(d)) {
                    if (!validateInput(modifiedObject[d])) {
                        invalidKey.push(t(`psp.${d}`))
                    }
                }
            })

            if (invalidKey?.length > 0) {
                log.create({ status: "error", description: "Bank detail update rejected: invalid characters", logData: { invalidKeys: invalidKey } })
                setNotificationMessage({
                    status: "error",
                    title: "",
                    msg: t("errMsg.specialCharactersAreNotAllowed", {field: invalidKey.join(", ")}),
                    key: "profileNotification",
                });
            } else {

                const response = updateBankInfo({
                    id: id || null,
                    user: userid,
                    formData: modifiedObject
                })

                if (response?.updateBankInformation?.__typename === "BaseError") {
                    log.create({ status: "error", description: `Bank detail update failed: ${response?.updateBankInformation?.errKey}`, logData: response?.updateBankInformation })
                    return await baseErrorChecking(response?.updateBankInformation?.errKey)

                } else {
                    const bankDetail = await getBankInfo(userid)
                    setNotificationMessage({
                        status: "success",
                        title: "",
                        msg: "Updated Successfully",
                        key: "profileNotification",
                    });
                    await getBankDetails()
                    setBankInfo(bankDetail?.getBankInformation?.data)
                    log.create({ status: "info", description: "Bank detail updated", logData: { ...modifiedObject } })
                    return {
                        status: "success",
                    }
                }
            }
        } catch (e) {
            log.create({ status: "error", description: "Bank detail update exception", logData: e })
            console.error("Error:", e)
            setNotificationMessage({
                status: "error",
                title: "",
                msg: "System Error",
                key: "systemNotification",
            });
        }

    }

    const updatePspBankDetail = async (userid, modifiedObject, id) => {
        const log = createLogSession("User", "Profile", "updatePspBankDetail")
        try {
            const invalidKey = []
            Object.keys(modifiedObject)?.map(d => {
                if (validateKeys.includes(d)) {
                    if (!validateInput(modifiedObject[d])) {
                        invalidKey.push(t(`psp.${d}`))
                    }
                }
            })

            if (invalidKey?.length > 0) {
                log.create({ status: "error", description: "PSP bank detail update rejected: invalid characters", logData: { invalidKeys: invalidKey } })
                setNotificationMessage({
                    status: "error",
                    title: "",
                    msg: t("errMsg.specialCharactersAreNotAllowed", {field: invalidKey.join(", ")}),
                    key: "profileNotification",
                });
            } else {
                const response = updateBankInfo({
                    psp: id || null,
                    user: userid,
                    formData: modifiedObject
                })

                if (response?.updateBankInformation?.__typename === "BaseError") {
                    log.create({ status: "error", description: `PSP bank detail update failed: ${response?.updateBankInformation?.errKey}`, logData: response?.updateBankInformation })
                    return await baseErrorChecking(response?.updateBankInformation?.errKey)

                } else {
                    const bankDetail = await getBankInfo(userid)
                    setNotificationMessage({
                        status: "success",
                        title: "",
                        msg: "Updated Successfully",
                        key: "profileNotification",
                    });
                    await getBankDetails()
                    setBankInfo(bankDetail?.getBankInformation?.data)
                    log.create({ status: "info", description: "PSP bank detail updated", logData: { ...modifiedObject } })
                    return {
                        status: "success",
                    }
                }
            }

        } catch (e) {
            log.create({ status: "error", description: "PSP bank detail update exception", logData: e })
            console.error("Error:", e)
            setNotificationMessage({
                status: "error",
                title: "",
                msg: "System Error",
                key: "systemNotification",
            });
        }
    }

    function categorizeObjectsByCategory(arr) {
        return arr?.reduce((obj, item) => {
            const {fieldList} = item;

            if (
                fieldList.category !== "basicInformation" &&
                fieldList.category !== "loginInformation" &&
                fieldList.category !== "registrationInformation" &&
                item.position !== null &&
                (userDetails?.clientAccountType !== "corporate" ? fieldList.category !== "corporateInformation" : true)
            ) {
                if (obj[fieldList.category]) {
                    obj[fieldList.category].push(item);
                } else {
                    obj[fieldList.category] = [item];
                }
            }

            return obj;
        }, {});
    }

    return {
        updateProfile,
        updateUserProfile,
        updateBankDetail,
        updatePspBankDetail,
        updatePassword,
        updateUserNewsletterSettings,
        categorizeObjectsByCategory
    }

}

export {
    useProfileFunction
}
